import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, firstValueFrom, map, take, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';

import { isEqual } from 'lodash';
import { AccessControl, Collaboration } from '@pncl/common-models';
import { PencilModalSizeEnum } from 'src/app/standalones/components/modals/modal-template/modal-template.component';
import {
  preventInvalidPhonePaste,
  applyUnparsedPhoneInputValidation,
} from 'src/app/utilities/phone-number.utils';
import { User, UserConstantsTransformer, UserTypes } from '../../../models/user';
import { UserService } from '../../../services/user.service';
import { UploadFileService } from '../../../services/upload-file.service';
import { SettingsService } from '../../../services/settings.service';
import { TelemetryService } from '../../../services/telemetry.service';
import { AclService } from '../../../services/acl.service';
import { CapabilityTierService } from '../../../services/capability-tier.service';
import { CustomerSupportService } from '../../../services/customer-support.service';
import { ModalManagerService } from '../../../services/modal-manager.service';
import { ResourceItemModel } from '../../../models/resource';
import { buildUserRoleOptions } from '../../../dialogs/user-info-input-dialog/user-info-options';
import { RadioGroupInputOption } from '../../../standalones/components/radio-group-input/radio-group-input.component';
import Capability = AccessControl.Capability;

@UntilDestroy()
@Component({
  selector: 'app-user-settings-account[form]',
  templateUrl: './user-settings-account.component.html',
  styleUrls: ['./user-settings-account.component.scss'],
})
export class UserSettingsAccountComponent implements OnInit {
  @Input() form!: UntypedFormGroup;
  @Input() isMobile = false;
  @Input() PANELS;
  @Output() goBack = new EventEmitter<void>();
  @Output() formChangedEvent: EventEmitter<boolean> = new EventEmitter();
  @ViewChild('phoneInput') phoneInput?: NgxMatIntlTelInputComponent;

  isUploadingImage = false;
  user?: User;
  isLoading = false;
  isPhoneFocused = false;
  infoForm?: UntypedFormGroup;
  ROLE_TYPE_IDS;
  detailsEditDisabled = true;
  roleOptions: RadioGroupInputOption[] = [];
  selectedRoleOption?: RadioGroupInputOption;
  public initialFormValue: unknown;
  public formChanged$ = new BehaviorSubject<boolean>(false);
  public hasSMSReminderCapability$ = this.capabilityTierService.shouldEnableCapability$(
    Capability.SMS_REMINDERS,
  );

  readonly MAX_ENTITY_TITLE_LENGTH = Collaboration.Constants.MAX_ENTITY_TITLE_LENGTH;
  readonly onPhonePaste = preventInvalidPhonePaste;

  constructor(
    private uploadService: UploadFileService,
    private userService: UserService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private telemetry: TelemetryService,
    private aclService: AclService,
    private customerSupportService: CustomerSupportService,
    private modalManagerService: ModalManagerService,
    private capabilityTierService: CapabilityTierService,
  ) {
    const userConstants = new UserConstantsTransformer(this.translateService);
    this.ROLE_TYPE_IDS = userConstants.roleTypeIDs;
    this.roleOptions = buildUserRoleOptions(this.translateService);
  }

  ngOnInit(): void {
    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      if (res) {
        this.user = res.user;
        this.detailsEditDisabled = this.shouldDisableEdit();
        this.initForm();
        if (this.detailsEditDisabled) {
          this.form.controls['name'].disable({ emitEvent: false }); // Need to prevent emitting event to not cause formChangd observable to detect changes in the form.
        }
      }
    });

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      applyUnparsedPhoneInputValidation(this.phoneInput, this.form.controls['phone']);
    });
  }

  // Determines if the current user has permission to edit their profile.
  shouldDisableEdit(): boolean {
    // Check if the current user is a student by looking for the Student persona in their profiles.
    const userIsStudent = this.aclService.isStudent(this.user as UserTypes);

    // Allow profile editing unless the institution settings explicitly prevent students from doing so.
    const isStudentDetailsEditingRestrictedByInstitution =
      this.user?.institution?.settings?.preventStudentsFromEditingDetails || false;

    // Allow profile editing if it's not restricted by the institution or if the user is not a student.
    return userIsStudent && isStudentDetailsEditingRestrictedByInstitution;
  }

  initForm(): void {
    this.form.removeControl('info');
    this.infoForm = undefined;
    if (!this.user?.institution) {
      this.infoForm = new UntypedFormGroup({
        role: new UntypedFormControl('', [Validators.required]),
        questionnaireComplete: new UntypedFormControl(true),
      });
      this.form.addControl('info', this.infoForm);
    }

    this.resetForm();
  }

  resetForm(): void {
    this.form.patchValue({ ...this.user });
    if (!this.user?.phone) {
      this.phoneInput?.reset();
      this.form.patchValue({ phone: '' });
    }

    this.initialFormValue = this.form.value;
    this.form.valueChanges
      .pipe(
        map(() => !isEqual(this.initialFormValue, this.form.value)),
        tap((x) => this.formChangedEvent.emit(x)),
        untilDestroyed(this),
      )
      .subscribe(this.formChanged$);

    this.updateSelectedRoleOption();
  }

  updateSelectedRoleOption(): void {
    const currentRole = this.infoForm?.controls['role']?.value;
    // Normalize 'other' to 'teacher' for legacy support
    const normalizedRole = currentRole === 'other' ? 'teacher' : currentRole;

    this.selectedRoleOption = this.roleOptions.find((option) => option.value === normalizedRole);
  }

  setRoleValue(option: RadioGroupInputOption): void {
    this.selectedRoleOption = option;
    this.infoForm?.controls['role']?.setValue(option.value);
    this.infoForm?.controls['role']?.markAsDirty();
    this.form.markAsDirty();
  }

  upload(event: Event): void {
    if (event.target instanceof HTMLInputElement && event.target.files && event.target.files[0]) {
      this.isUploadingImage = true;
      const uploadTask = this.uploadService.uploadToFireStorage(
        event.target.files[0],
        (data) => {
          this.form.patchValue({ profile_image: data });
          this.form.markAsDirty();
          this.isUploadingImage = false;
        },
        undefined, // onFailure callback (optional)
        false, // appendFileNameToId (optional)
        undefined, // maxFileSizeInMB (optional, will use default from service)
        ResourceItemModel.IMAGE, // expectedResourceType - THIS IS THE NEW ARGUMENT
      );

      // If uploadTask is undefined, file validation failed (e.g., size exceeded or wrong type)
      // Reset uploading state to show the button again
      if (!uploadTask) {
        this.isUploadingImage = false;
      }
    }
  }

  deleteAccount(): void {
    const userEmail = this.userService.user.value?.user?.email ?? '';
    const message = `Hi, I would like to request deletion of my account. My email is ${userEmail}. Please help me with this process.`;
    this.customerSupportService.openSupport({
      showCommand: 'showNewMessage',
      target: message,
    });
  }

  async submitChanges(): Promise<void> {
    if (!this.user || this.isLoading) {
      return;
    }

    // Trim name field before submission
    this.form.get('name')?.setValue(this.form.value.name.trim());

    const formValue = this.form.value;
    if (formValue.info) {
      formValue.info.roleType = this.ROLE_TYPE_IDS.education;
    }

    const updatedUser = {
      ...this.user,
      ...formValue,
      // Resolve phone updates explicitly: save provided values, send '' when
      // clearing an existing phone, or undefined when no phone was ever set.
      phone: formValue.phone || (this.user.phone ? '' : undefined),
    };

    // If the user has a phone number and has not consented to receive SMS reminders, show the consent modal
    if (
      this.hasSMSReminderCapability$ &&
      updatedUser.phone &&
      !this.user.phone_number_consent_accepted
    ) {
      const confirmed = await firstValueFrom(
        this.modalManagerService.showConsentModal({
          title: this.translateService.instant('Add phone number?'),
          description: this.translateService.instant(
            "We'll use this phone number to send SMS reminders for <br> events on your calendar. Message and data rates apply.<br><br>",
          ),
          consentCheckboxLabel: this.translateService.instant(
            'I agree to receive event reminders by SMS.',
          ),
          size: PencilModalSizeEnum.MEDIUM,
        }),
      );

      if (!confirmed) {
        return;
      }

      updatedUser.phone_number_consent_accepted = true;
    }

    this.isLoading = true;
    this.userService
      .updateUser(updatedUser)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.telemetry.event('user_role_data_provided', {
            roleType: updatedUser.info?.roleType,
            role: updatedUser.info?.role,
            roleOther: updatedUser.info?.roleOther,
          });
          this.settingsService.showToast({
            title: this.translateService.instant('Account updated'),
          });
          if (this.isMobile) {
            this.goBack.emit();
          }
        },
        error: () => {
          this.settingsService.showToast(
            {
              title: this.translateService.instant('Update failed'),
              description: this.translateService.instant('An error occurred. Please try again.'),
            },
            true,
          );
        },
        complete: () => {
          this.isLoading = false;
          this.formChanged$.next(false);
          this.formChangedEvent.emit(false);
        },
      });
  }
}
